import React, { useState, useEffect } from 'react';
import { useQuiz } from '../contexts/QuizContext';
import AnswerOption from './AnswerOption';
import { motion, AnimatePresence } from 'framer-motion';

const QuestionCard = ({ question }) => {
  const { saveAnswer, nextQuestion } = useQuiz();
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  
  useEffect(() => {
    // Resetar a animação quando a pergunta mudar
    setIsAnimating(false);
    setSelectedOption(null);
    setInputValue('');
    setInputError('');
  }, [question]);

  const handleOptionSelect = (optionId, optionText) => {
    setSelectedOption(optionId);
    saveAnswer(question.id, optionText);
  };

  const isInputQuestion = ['text', 'email', 'phone'].includes(question?.type);
  const isButtonsQuestion = question?.type === 'buttons';

  const validateInput = (type, value) => {
    const trimmed = value.trim();
    if (type === 'text') {
      return trimmed.length >= 2;
    }
    if (type === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    }
    if (type === 'phone') {
      const digits = trimmed.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 11; // DDD + número
    }
    return false;
  };

  const handleInputChange = (e) => {
    let v = e.target.value;
    if (question.type === 'phone') {
      v = v.replace(/[^0-9]/g, ''); // apenas dígitos
    }
    setInputValue(v);
    setInputError('');
  };

  const handleNextQuestion = () => {
    if (isInputQuestion) {
      if (!validateInput(question.type, inputValue)) {
        if (question.type === 'text') setInputError('Por favor, informe seu nome.');
        else if (question.type === 'email') setInputError('Informe um e-mail válido.');
        else if (question.type === 'phone') setInputError('Informe DDD + número (10 ou 11 dígitos).');
        return;
      }
      let valueToSave = inputValue.trim();
      if (question.type === 'phone') {
        const digits = valueToSave.replace(/\D/g, '');
        valueToSave = `${question.countryCode || '+55'} ${digits}`;
      }
      saveAnswer(question.id, valueToSave);
      setIsAnimating(true);
      setTimeout(() => {
        nextQuestion();
        setIsAnimating(false);
        setInputValue('');
        setInputError('');
      }, 300);
      return;
    }

    if (selectedOption) {
      setIsAnimating(true);
      // Adiciona um pequeno atraso para a animação ser visível
      setTimeout(() => {
        nextQuestion();
        setSelectedOption(null);
        setIsAnimating(false);
      }, 300);
    }
  };

  const isContinueEnabled = isInputQuestion ? validateInput(question.type, inputValue) : !!selectedOption;

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        key={question.id}
        className="question-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <motion.h2 
          className="text-xl font-bold text-gray-800 mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {question.text}
        </motion.h2>
        {isButtonsQuestion ? (
          <div className="flex flex-wrap gap-4 justify-between w-full mb-6">
            {question.options.map((option, index) => (
              <motion.button
                key={option.id}
                className="btn-primary flex-1 min-w-[140px] w-full sm:w-auto"
                style={{ flex: '1 0 calc(50% - 8px)' }}
                onClick={() => {
                  saveAnswer(question.id, option.text);
                  setIsAnimating(true);
                  setTimeout(() => {
                    nextQuestion();
                    setIsAnimating(false);
                  }, 300);
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={isAnimating}
              >
                {option.text}
              </motion.button>
            ))}
          </div>
        ) : isInputQuestion ? (
          <div className="mb-6">
            {question.type === 'phone' ? (
              <div>
                <div className="flex items-center space-x-3">
                  <div className="px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-700">
                    {question.countryCode || '+55'}
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder={question.placeholder || '(DDD) 9XXXX-XXXX'}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleNextQuestion()}
                  />
                </div>
                <p className="text-gray-500 text-sm mt-2">Informe DDD + número (somente dígitos). Ex: 11987654321</p>
              </div>
            ) : (
              <input
                type={question.type === 'email' ? 'email' : 'text'}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder={question.placeholder || ''}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={(e) => e.key === 'Enter' && handleNextQuestion()}
              />
            )}
            {inputError && <p className="text-red-600 text-sm mt-2">{inputError}</p>}
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {question.options.map((option, index) => (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <AnswerOption
                  option={option}
                  isSelected={selectedOption === option.id}
                  onSelect={handleOptionSelect}
                />
              </motion.div>
            ))}
          </div>
        )}
        
        {!isButtonsQuestion && (
          <motion.button
            className={`btn-primary ${!isContinueEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleNextQuestion}
            disabled={!isContinueEnabled || isAnimating}
            whileHover={isContinueEnabled ? { scale: 1.03 } : {}}
            whileTap={isContinueEnabled ? { scale: 0.97 } : {}}
            animate={isAnimating ? { opacity: [1, 0.5, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            {isAnimating ? 'Processando...' : 'Continuar'}
          </motion.button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default QuestionCard;
